import React, { useState, useEffect, useCallback } from "react";
import { Grid, Box, Typography, Alert, Container } from "@mui/material";
import CardComponent from "../../../components/plan_card";
import { useNavigate, useLocation } from "react-router-dom";
import PriceDetails from "./price_modal";
import BusinessDetails from "../business_details";
import { makeStyles } from "@mui/styles";
import subscriptionApi from "../../../services/subscribe";
import { useDispatch, useSelector } from "react-redux";
import { showLoader, hideLoader } from "../../../redux/loader/actions";
import { getDecryptedCookie, removeCookie } from "../../../utils";

const useStyles = makeStyles({
});

const CardSelectionPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const organizationItems = useSelector(
    (state) => state.organizationReducer.items
  );
  const findId = getDecryptedCookie("upid");
  const classes = useStyles();
  const [selectedCard, setSelectedCard] = useState(null);
  const [trialMessage, setTrialMessage] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [productUUID, setProductUUID] = useState("");
  const [planData, setPlanData] = useState([]);
  const [filteredPlans, setFilteredPlans] = useState([]);
  const [actionType, setActionType] = useState("");
  const [showTrialDetails, setShowTrialDetails] = useState(false);
  const [selectedPlanDetails, setSelectedPlanDetails] = useState([]);
  const [productDetails, setProductDetails] = useState({});
  const [pricing, setPricing] = useState(null);
  const [planOrder, setPlanOrder] = useState();
  const [upgradePlansAvailable, setUpgradePlansAvailable] = useState(false);
  const [selectedPlanIndex, setSelectedPlanIndex] = useState(null);
  const [type, setType] = useState();
  const cardColors = ["#FFEBEE", "#E8F5E9", "#E3F2FD", "#F3E5F5", "#FFF3E0"];
  const borderColors = ["#FF4081", "#4CAF50", "#1976D2", "#9C27B0", "#FF9800"];

  useEffect(() => {
    const uuid = getDecryptedCookie("proid");
    const action = getDecryptedCookie("action")?.toLowerCase();
    const type = getDecryptedCookie("type");

    setType(type);
    setActionType(action);
    if (action === "trial") {
      setTrialMessage(
        "Experience 14 Days of Free Access—Discover, Delight, and Enjoy!"
      );
      setShowTrialDetails(true);
    }
    setProductUUID(uuid);
  }, [location.search, organizationItems]);

  useEffect(() => {
    removeCookie("upid");
  }, []);

  const getFilteredPlans = (plans, planOrder, actionType, type) => {
    const sortedPlans = [...plans].sort(
      (a, b) => (a?.plan_order || 0) - (b?.plan_order || 0)
    );

    if (actionType === "upgrade" && planOrder) {
      const availablePlans = sortedPlans.filter(
        (plan) => (plan?.plan_order || 0) > (planOrder || 0) 
      );
      if (typeof setUpgradePlansAvailable === "function") {
        setUpgradePlansAvailable(availablePlans.length > 0);
      }

      return availablePlans.map((plan) => ({
        ...plan,
        isSelected: false,
      }));
    }

    if (type === "trials") {
      return sortedPlans
        .filter((plan) => (plan?.plan_order || 0) >= (planOrder || 0))
        .map((plan) => ({
          ...plan,
          isSelected: plan?.plan_order === planOrder,
        }));
    }

    return sortedPlans.map((plan) => ({
      ...plan,
      isSelected: plan?.plan_order === planOrder,
    }));
  };

  const findingSubscription = useCallback((id) => {
    const onSuccess = (res) => {
      if (res?.data?.status) {
        setPlanOrder(res?.data?.data?.productPlanDetails?.plan_order);
      }
    };
    const onFailure = (err) => {
      console.log(err?.response?.data?.msg);
    };
    subscriptionApi?.findSubscriptions(id).then(onSuccess, onFailure);
  }, []);

  useEffect(() => {
    findingSubscription(findId);
  }, [findId, organizationItems]);

  const getPlanDetails = () => {
    dispatch(showLoader());
    subscriptionApi
      .getPlanFeatures(productUUID)
      .then((res) => {
        dispatch(hideLoader());
        const plans = res.data.data;
        const filtered = getFilteredPlans(plans, planOrder, actionType, type);
        setFilteredPlans(filtered);
        setProductDetails(res.data.product);
      })
      .catch((err) => {
        dispatch(hideLoader());
        console.error("Error response:", err);
      });
  };

  useEffect(() => {
    if (productUUID && actionType) {
      getPlanDetails();
    }
  }, [productUUID, planOrder, actionType, organizationItems, type]);

  const handleCardClick = (cardIndex) => {
    setSelectedPlanDetails(filteredPlans[cardIndex]);
    setSelectedCard(cardIndex);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedCard(null);
    setSelectedPlanDetails([])
    setSelectedPlanIndex(null)
    setPricing(null)
  };
  const handleClose = () => {
    setModalOpen(false);
    setSelectedCard(null);
  };

  const handleModalSubmit = () => {
    if (actionType === "new") {
      setModalOpen(false);
      setShowTrialDetails(true);
    } else {
      setModalOpen(false);
      navigate("/products/pricing");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Container>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 4 }}
        >
          <Grid item>
            <Typography
              variant="h4"
              component="div"
              sx={{
                fontSize: { xs: "24px", sm: "34px", md: "inherit" },
              }}
            >
              Choose Your Plan
            </Typography>
          </Grid>
          <Grid item>
            {trialMessage && (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Alert variant="outlined" severity="success" sx={{ ml: 4 }}>
                  {trialMessage}
                </Alert>
              </Box>
            )}
          </Grid>
        </Grid>
      </Container>

      <Grid container spacing={4} justifyContent="center">
        {filteredPlans?.length > 0 ? (
          filteredPlans.map((card, index) => (
            <Grid item xs={12} sm={12} md={4} lg={3} key={index}>
              <CardComponent
                title={{
                  text: card?.product_plan_name || "Unknown Plan",
                  color: "#000",
                }}
                price={card?.frequency_price || card?.frequency}
                features={(card?.features || [])?.map(
                  (feature) => feature?.plan_characteristic_name || ""
                )}
                isSelected={card.isSelected} 
                initialBgColor={cardColors[index % cardColors.length]}
                selectedBorderColor={borderColors[index % borderColors.length]}
                onClick={() => handleCardClick(index)}
                isPopular={index === 2}
              />
              {card.isSelected && ( 
                <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                  Active
                </Typography>
              )}
            </Grid>
          ))
        ) : upgradePlansAvailable ? (
          <Grid item xs={12}>
            <Typography>No plans available at the moment.</Typography>
          </Grid>
        ) : (
          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: "17px",
                textAlign: "center",
                color: "black",
                width: "400px",
              }}
            >
              Currently, no plans are available for your account. Please check
              back later for updates.
            </Typography>
          </Grid>
        )}
      </Grid>

      {showTrialDetails ? (
        <BusinessDetails
          open={modalOpen}
          onClose={handleCloseModal}
          onSubmit={handleModalSubmit}
          trialMessage={trialMessage}
          actionType={actionType}
          selectedPlanDetails={selectedPlanDetails}
          onBackToPriceDetails={() => {
            setModalOpen(true);
          }}
          productDetails={productDetails}
          pricing={pricing}
          setPricing={setPricing}
          setShowTrialDetails={setShowTrialDetails}
          setModalOpen={setModalOpen}
        />
      ) : null}

      {!showTrialDetails ? (
        <PriceDetails
          open={modalOpen}
          onClose={handleCloseModal}
          onClosed={handleClose}
          onSubmit={handleModalSubmit}
          trialMessage={trialMessage}
          productDetails={productDetails}
          setProductDetails={setProductDetails}
          selectedPlanDetails={selectedPlanDetails}
          setSelectedPlanDetails={setSelectedPlanDetails}
          setShowTrialDetails={setShowTrialDetails}
          pricing={pricing}
          setPricing={setPricing}
          actionType={actionType}
          setModalOpen={setModalOpen}
          selectedPlanIndex={selectedPlanIndex}
          setSelectedPlanIndex={setSelectedPlanIndex}
          setSelectedCard={setSelectedCard}
        />
      ) : null}
    </Box>
  );
};

export default CardSelectionPage;
